import { zodResolver } from "@hookform/resolvers/zod"
import { Modal, InputText, InputValidation, Button, Select, Option, DatePicker } from "@jobber/components"
import { forwardRef, useState, useImperativeHandle, useEffect } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { trpc } from "@/services/trpc"
import { formatDate } from "@/utils"

type Props = {
  onSubmit: (values: TimeOffFormResult) => void
}

type RefFields = {
 openModal: (timeOff?: any) => void
}

type TimeOffFormResult = {
  id?: string | null
  reason: string
  serviceAreaId: string
  start: string
  end: string
}

const formSchema = z.object({
  reason: z.string().min(1, "Reason is required"),
  serviceAreaId: z.string().min(1, "Service area is required"),
  start: z.date().refine((val) => val !== null, {
    message: "Date is required"
  }),
  end: z.date({required_error: 'Date is required'})
})

const CreateTimeOffModal = forwardRef(({ onSubmit }: Props, ref: React.Ref<RefFields>) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [timeOffData, setTimeOffData] = useState<any>(null)

  const {data: serviceAreas} = trpc.serviceAreas.list.useQuery()

  useImperativeHandle(ref, () => ({
    openModal: (timeOff?: TimeOffFormResult) => {
      if (timeOff) {
        if (timeOff.id === undefined || timeOff.id === null) timeOff.id = null
        else setTimeOffData(timeOff)
        form.setValue('reason', timeOff.reason)
        form.setValue('serviceAreaId', timeOff.serviceAreaId)
        form.setValue('start', new Date(timeOff.start))
        form.setValue('end', new Date(timeOff.end))
      }
      setModalOpen(true)
    }
  }))
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      reason: '',
      serviceAreaId: '',
      start: new Date(),
      end: new Date(),
    }
  })

  useEffect(() => {
    if (!modalOpen) {
      setTimeOffData(null)
      form.reset()
    }
  }, [modalOpen])

  const onFormSubmit = (values: z.infer<typeof formSchema>) => {
    const returnObj: TimeOffFormResult = {
      reason: values.reason,
      serviceAreaId: values.serviceAreaId,
      start: values.start?.toISOString(),
      end: values.end?.toISOString(),
    }
    if (timeOffData?.id !== undefined && timeOffData?.id !== null) returnObj.id = timeOffData.id
    onSubmit(returnObj)
    setModalOpen(false)
  }
  return (
    <Modal
      title={timeOffData?.id ? `Edit "${timeOffData.name}" Time Off` : "Create new Time Off"}
      open={modalOpen}
      onRequestClose={() => setModalOpen(false)}
    >
      <form
        style={{ padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <div>
          <InputText
            placeholder={"Time off reason"}
            value={form.watch('reason')}
            onChange={(val) => form.setValue('reason', val as string)}
          />
          {form.formState.errors.reason && <InputValidation message={form.formState.errors.reason.message as string} />}
        </div>
        <div>
          <Select placeholder="Select service area" value={form.watch('serviceAreaId')} onChange={(val) => form.setValue('serviceAreaId', val as string)}>
            <Option value=""></Option>
            {serviceAreas?.map((sa: any) => (
              <Option key={sa.id} value={sa.id}>{sa.name}</Option>
            ))}
          </Select>
          {form.formState.errors.serviceAreaId && <InputValidation message={form.formState.errors.serviceAreaId?.message as string} />}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{width: '100%'}}>
            <div>Start Date:</div>
            <DatePicker
              onChange={(date: Date) => form.setValue('start', date)}
              selected={form.watch('start')}
              activator={<Button variation="subtle" label={form.watch('start') ? `${formatDate(form.watch('start'))}` : "Select start date"} />}
            />
            {form.formState.errors.start && <InputValidation message={form.formState.errors.start?.message as string} />}
          </div>
          <div style={{width: '100%'}}>
            <div>End Date:</div>
            <DatePicker
              onChange={(date: Date) => form.setValue('end', date)}
              selected={form.watch('end')}
              activator={<Button variation="subtle" label={form.watch('end') ? `${formatDate(form.watch('end'))}` : "Select end date"} />}
            />
            {form.formState.errors.end && <InputValidation message={form.formState.errors.end?.message as string} />}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            submit
            variation="learning"
            label="Create"
            loading={false}
            disabled={false}
          />
        </div>
      </form>
    </Modal>
  )
})

export default CreateTimeOffModal

export type {RefFields, TimeOffFormResult}
